<template>
	<PrecoaModal
		v-model:open="open"
		class="sm:w-auto! sm:min-w-sm md:max-w-md"
	>
		<SubmissionFetchError
			v-if="statusFetchFailed"
			:message="t('home.submissions.fetchStatusError')"
		/>
		<template v-else-if="statusLoaded">
			<div class="flex flex-col gap-2 px-2 py-4 text-center">
				<p class="font-bold text-slate-900">
					{{ t('home.submissions.statusModalTitle') }}
				</p>
				<p class="text-sm text-slate-500">
					{{ t('home.submissions.statusModalSubtitle') }}
				</p>
			</div>
			<div class="flex flex-col gap-4">
				<div
					v-for="{ id, name } in displayedPlans"
					:key="id"
				>
					<p class="mb-2 pl-1 text-sm font-semibold">{{ name }}</p>
					<SubmissionStatusCard
						@refresh-status="refreshStatus"
						:planId="id"
						:planSubmissions="getSubmissionStatus(id)"
					/>
				</div>
			</div>
		</template>
		<Loading v-else></Loading>
	</PrecoaModal>
</template>

<script setup lang="ts">
import type { SubmissionStatusGroup } from '@/classes/SubmissionStatuses/types'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import Loading from '@/components/documents/Loading.vue'
import SubmissionFetchError from '@/components/home/SubmissionStatuses/SubmissionFetchError.vue'
import SubmissionStatusCard from '@/components/home/SubmissionStatuses/SubmissionStatusCard.vue'
import { useSubmissionStatusService } from '@/services/submissionStatusService'
import { useSubmissionStore } from '@/stores/submission'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { getStatusForPlanIds } = useSubmissionStatusService()
const { t } = useI18n()

const submissionStore = useSubmissionStore()

const submissionStatuses = ref<Record<string, SubmissionStatusGroup>>()

const props = defineProps({
	plans: {
		type: Array<{ id: string; name: string }>,
		required: true,
	},
	onlyShowErrors: {
		type: Boolean,
		default: false,
	},
})

const open = defineModel<boolean>('open')
const statusLoaded = ref<boolean>(false)
const statusFetchFailed = ref<boolean>(false)

const displayedPlans = computed(() => {
	if (!props.onlyShowErrors) {
		return props.plans
	}

	return props.plans.filter(
		(plan) => submissionStore.getSubmissionErrorCount(plan.id) > 0
	)
})

watch(open, async (modalOpenValue) => {
	if (modalOpenValue) {
		refreshStatus().catch()
	}
})

const refreshStatus = async () => {
	try {
		statusFetchFailed.value = false
		statusLoaded.value = false
		await checkSubmissionStatus()
		statusLoaded.value = true
	} catch (e) {
		statusFetchFailed.value = true
	}
}

// Fetch the statuses
const checkSubmissionStatus = async () => {
	const ids = props.plans.map((plan) => plan.id)
	const response = await getStatusForPlanIds(ids)
	const statuses: Record<string, SubmissionStatusGroup> = Object.fromEntries(
		Object.entries(response).map(([planId, result]) => [
			planId,
			result.statuses,
		])
	)

	submissionStatuses.value = statuses
	submissionStore.syncRemoteSubmissionErrors(statuses)
}

// look up a status we have already fetched. Helper to keep template clean
const getSubmissionStatus = (id: string): SubmissionStatusGroup => {
	return submissionStatuses.value
		? submissionStatuses.value[id.toUpperCase()]
		: {}
}
</script>
